import React, { useState } from 'react';

interface FractionalSharesEducationProps {
  className?: string;
  currentFractionalShares?: number;
  currentFractionalUSDT?: number;
  currentSharePrice?: number;
}

export const FractionalSharesEducation: React.FC<FractionalSharesEducationProps> = ({
  className = '',
  currentFractionalShares = 0,
  currentFractionalUSDT = 0,
  currentSharePrice = 15
}) => {
  const [activeExample, setActiveExample] = useState<'shares' | 'usdt'>('shares');

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-xl p-6 text-white shadow-lg">
        <h2 className="text-2xl sm:text-3xl font-bold mb-2">
          💎 Understanding Fractional Shares & USDT
        </h2>
        <p className="text-blue-100 text-sm sm:text-base">
          Learn how fractional commissions work and why they're valuable for your long-term growth
        </p>
      </div>

      {/* What Are Fractional Shares? */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span className="text-2xl">🔍</span>
          What Are Fractional Shares & USDT?
        </h3>
        
        <div className="space-y-4 text-gray-300">
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
            <h4 className="font-semibold text-blue-300 mb-2">Fractional Shares</h4>
            <p className="text-sm">
              When you earn commissions from your <strong>direct sponsors/referrals</strong>, you receive both USDT and shares. 
              These shares are often fractional (less than 1.0) because they're calculated as a percentage of the purchase amount.
            </p>
          </div>

          <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <h4 className="font-semibold text-green-300 mb-2">Fractional USDT</h4>
            <p className="text-sm">
              Similarly, USDT commissions can have fractional amounts (cents and fractions of cents).
              These accumulate from affiliate commission earnings on your direct referrals.
            </p>
          </div>

          <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-300 mb-2">⚠️ Important Distinction</h4>
            <p className="text-sm">
              <strong>Whole shares</strong> (1, 2, 3, etc.) are complete ownership units.<br/>
              <strong>Fractional shares</strong> (0.025, 0.125, etc.) still have value until they accumulate to form whole shares.
            </p>
          </div>
        </div>
      </div>

      {/* Your Current Fractions */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span className="text-2xl">📊</span>
          Your Current Fractional Holdings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
            <div className="text-sm text-blue-300 mb-1">Fractional Shares</div>
            <div className="text-2xl font-bold text-blue-400 mb-2">
              {currentFractionalShares.toFixed(6)}
            </div>
            <div className="text-sm text-white">
              Value: ${(currentFractionalShares * currentSharePrice).toFixed(2)} USD
            </div>
            <div className="text-sm text-white mt-1">
              Need {(1 - currentFractionalShares).toFixed(6)} more to reach 1 whole share
            </div>
          </div>

          <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <div className="text-sm text-green-300 mb-1">Fractional USDT</div>
            <div className="text-2xl font-bold text-green-400 mb-2">
              ${currentFractionalUSDT.toFixed(6)}
            </div>
            <div className="text-sm text-white">
              Withdrawable once you reach $1.00
            </div>
            <div className="text-sm text-white mt-1">
              Need ${(1 - currentFractionalUSDT).toFixed(6)} more to reach $1
            </div>
          </div>
        </div>
      </div>

      {/* How Fractions Accumulate */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span className="text-2xl">📈</span>
          How Fractional Commissions Accumulate
        </h3>

        {/* Tab Selector */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveExample('shares')}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
              activeExample === 'shares'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Share Example
          </button>
          <button
            onClick={() => setActiveExample('usdt')}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
              activeExample === 'usdt'
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            USDT Example
          </button>
        </div>

        {/* Share Example */}
        {activeExample === 'shares' && (
          <div className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-white mb-3">Example: Building to 1 Whole Share</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">Your direct sponsor makes a $10 purchase</div>
                    <div className="text-white">You earn: 10% USDT ($1.00) + 5% shares (0.05 shares)</div>
                    <div className="text-blue-300 text-sm mt-1">Fractional shares: 0.05</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">Your sponsor's sponsor earns commission</div>
                    <div className="text-white">You earn: 10% USDT ($0.05) + 5% shares (0.0025 shares)</div>
                    <div className="text-blue-300 text-sm mt-1">Fractional shares: 0.05 + 0.0025 = 0.0525</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">After 20 similar transactions...</div>
                    <div className="text-white">Your fractional shares accumulate to 1.05 shares</div>
                    <div className="text-green-300 text-sm mt-1 font-semibold">✅ You now have 1 whole share + 0.05 fractional</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-yellow-400 text-xl">💡</span>
                <div className="text-sm text-yellow-200">
                  <strong>Key Point:</strong> Fractional shares have real value! They can be converted to USDT, 
                  transferred to others, or held until they accumulate into whole shares.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* USDT Example */}
        {activeExample === 'usdt' && (
          <div className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-white mb-3">Example: Building to $1.00 USDT</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">Your direct sponsor makes a $5 purchase</div>
                    <div className="text-white">You earn: 10% USDT = $0.50</div>
                    <div className="text-green-300 text-sm mt-1">Fractional USDT: $0.50</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">Another direct referral makes a $2.50 purchase</div>
                    <div className="text-white">You earn: 10% of $2.50 = $0.25</div>
                    <div className="text-green-300 text-sm mt-1">Fractional USDT: $0.50 + $0.25 = $0.75</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">Another direct referral makes a $10 purchase</div>
                    <div className="text-white">You earn: 10% USDT = $1.00</div>
                    <div className="text-green-300 text-sm mt-1 font-semibold">✅ Total: $0.75 + $1.00 = $1.75 (withdrawable!)</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-blue-400 text-xl">💰</span>
                <div className="text-sm text-blue-200">
                  <strong>Withdrawal Tip:</strong> While fractional USDT accumulates, you can withdraw once your total 
                  balance reaches the minimum withdrawal amount (typically $1.00 or more).
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legal Terminology */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span className="text-2xl">⚖️</span>
          Important Legal Terminology
        </h3>

        <div className="space-y-3 text-sm text-gray-300">
          <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <div className="font-semibold text-green-300 mb-2">✅ Our Single-Level Affiliate Program</div>
            <ul className="space-y-1 ml-4">
              <li>• <strong>Direct Referral:</strong> The person you directly referred using your link</li>
              <li>• <strong>Sponsor:</strong> The person who referred you</li>
              <li>• <strong>One-time Commission:</strong> 10% USDT + 5% automatic share purchase on direct referrals</li>
            </ul>
          </div>

          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
            <div className="text-blue-200 text-sm">
              <strong>Why This Matters:</strong> Our commission structure is a legitimate single-level affiliate program where you earn
              a one-time commission when your <strong>direct referrals make purchases</strong>. You do NOT earn from your referrals' referrals -
              this is a simple, legal affiliate structure, not multi-level marketing.
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span className="text-2xl">❓</span>
          Frequently Asked Questions
        </h3>

        <div className="space-y-4">
          <div className="border-b border-gray-700 pb-4">
            <h4 className="font-semibold text-white mb-2">Do fractional shares have value?</h4>
            <p className="text-sm text-gray-300">
              Yes! Fractional shares have the same per-share value as whole shares. You can convert them to USDT, 
              transfer them to others, or hold them until they accumulate into whole shares.
            </p>
          </div>

          <div className="border-b border-gray-700 pb-4">
            <h4 className="font-semibold text-white mb-2">When do fractional shares become whole shares?</h4>
            <p className="text-sm text-gray-300">
              Automatically! As you earn more commissions, your fractional shares add up. Once they total 1.0 or more, 
              you have whole shares.
            </p>
          </div>

          <div className="border-b border-gray-700 pb-4">
            <h4 className="font-semibold text-white mb-2">Can I withdraw fractional USDT?</h4>
            <p className="text-sm text-gray-300">
              Yes, once your total USDT balance (whole + fractional) reaches the minimum withdrawal amount. 
              The fractions are included in your total withdrawable balance.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-2">How long does it take to accumulate whole shares?</h4>
            <p className="text-sm text-gray-300">
              It depends on your sponsors' activity. The more active your direct sponsors are (making purchases and earning commissions), 
              the faster your fractional shares accumulate. Even small fractions add up over time!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

